// src/pages/WartaDetails.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../../api/client';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';

const WartaDetails = () => {
    const { id } = useParams();
    const [warta, setWarta] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isCancelled = false;
        let progressInterval;
        const MIN_LOADING_TIME = 1000;
        const DELAY_AFTER_LOAD = 700;

        const fetchWartaDetails = async () => {
            setIsLoading(true);
            setProgress(0);
            setError(null);

            const startTime = Date.now();
            progressInterval = setInterval(() => {
                const elapsedTime = Date.now() - startTime;
                let calculatedProgress = (elapsedTime / MIN_LOADING_TIME) * 100;
                if (calculatedProgress > 99) calculatedProgress = 99;
                if (!isCancelled) {
                    setProgress(Math.floor(calculatedProgress));
                }
            }, 50);

            try {
                const [wartaData] = await Promise.all([
                    apiClient.getWartaJemaatDetail(id),
                    new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME))
                ]);

                if (!isCancelled) {
                    if (wartaData) {
                        setWarta(wartaData);
                    } else {
                        setError('Warta tidak ditemukan.');
                    }
                }
            } catch (err) {
                if (!isCancelled) {
                    const errorMessage = err.message || 'Gagal mengambil detail warta jemaat.';
                    setError(errorMessage);
                    console.error('API Fetch Error:', err);
                }
            } finally {
                clearInterval(progressInterval);
                if (!isCancelled) {
                    setProgress(100);
                    setTimeout(() => {
                        setIsLoading(false);
                    }, DELAY_AFTER_LOAD);
                }
            }
        };

        if (id) {
            fetchWartaDetails();
        }

        return () => {
            isCancelled = true;
            clearInterval(progressInterval);
        };
    }, [id]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <LoadingSpinner progress={progress} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center">
                <h1 className="text-3xl font-bold text-red-600">{error}</h1>
                <Link to="/warta-jemaat" className="mt-4 inline-block bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300">
                    Kembali ke daftar warta
                </Link>
            </div>
        );
    }

    if (!warta) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center">
                <h1 className="text-3xl font-bold text-gray-800">Warta tidak ditemukan.</h1>
                <Link to="/warta-jemaat" className="mt-4 inline-block bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-300">
                    Kembali ke daftar warta
                </Link>
            </div>
        );
    }

    // Mengambil tanggal dari created_at
    const wartaDate = warta?.created_at.split(' ')[0];

    return (
        <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <PageTransitionWrapper>
                <div className="max-w-4xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12 transform transition-transform duration-300">
                        {/* Judul Halaman */}
                        <div className="text-center mb-8">
                            <h1 className="text-4xl sm:text-5xl font-extrabold border-b-4 border-blue-500 pb-4 tracking-tight text-blue-900">
                                {warta.title.slice(0, 12).toUpperCase()}
                                <br />
                                {warta.title.slice(12).toUpperCase()}
                            </h1>
                        </div>

                        {/* Wrapper untuk Detail Warta dan Tombol Aksi */}
                        <div className="flex flex-col md:flex-row md:items-start justify-between mb-8 border-b pb-4 border-gray-200">
                            {/* Bagian detail di kiri */}
                            <div className="text-left mb-4 md:mb-0 mr-4">
                                <p className="text-base text-gray-600 font-medium mt-2">
                                    Tanggal Rilis : <span className="text-blue-600 font-semibold">{wartaDate}</span>
                                </p>
                            </div>

                            {/* Wrapper untuk tombol Download dan Kembali */}
                            <div className="flex flex-row space-x-3 flex-shrink-0 pt-1">
                                {/* Tombol Download */}
                                <a
                                    href={warta.file}
                                    download={`${warta.title.replace(/ /g, '_')}_${wartaDate?.replace(/-/g, '_')}.pdf`}
                                    className="inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition-all duration-300 text-base"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                    Download
                                </a>

                                {/* Tombol Kembali */}
                                <Link
                                    to="/warta-jemaat"
                                    className="inline-flex items-center justify-center bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg shadow-md transition-all duration-300 text-base"
                                >
                                    &larr; Kembali
                                </Link>
                            </div>
                        </div>

                        {/* PDF Viewer */}
                        <iframe
                            src={warta.file}
                            width="100%"
                            height="700"
                            title={warta.title}
                            className="border-0 rounded-xl"
                            style={{ aspectRatio: '1 / 1.414' }} // Mempertahankan rasio aspek A4
                        >
                            <p>Browser Anda tidak mendukung iframe. Silakan klik <a href={warta.file} className="text-blue-600 hover:underline">link ini</a> untuk mengunduh file PDF.</p>
                        </iframe>
                    </div>
                </div>
            </PageTransitionWrapper>
        </div>
    );
};

export default WartaDetails;